/* Vision Attire: recolour the supplied studio photographs, preserving their
   silhouette, stitching and texture. Maps are built by prepare-builder-photos.py,
   prepare-style-assets.py and prepare-feedback-assets.py (logo outline, stripe bands
   and the right-leg stripe placement). */
const VA_PHOTO = (() => {
  'use strict';
  const cache = new Map(), pending = new Map(), requests = new WeakMap(), painted = new WeakMap();
  let manifestPromise;
  const manifest = () => manifestPromise || (manifestPromise = fetch(VA_DATA('assets/img/builder-photos/manifest.json'))
    .then(r => { if (!r.ok) throw new Error('Preview manifest unavailable'); return r.json(); })
    .catch(e => { manifestPromise = null; throw e; }));
  const pixels = url => new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => {
      const c = document.createElement('canvas'); c.width = im.naturalWidth; c.height = im.naturalHeight;
      const ctx = c.getContext('2d', {willReadFrequently:true}); ctx.drawImage(im,0,0);
      resolve({w:c.width,h:c.height,data:ctx.getImageData(0,0,c.width,c.height).data});
    };
    im.onerror = () => reject(new Error('Could not load garment photograph'));
    im.src = VA_DATA(url);
  });
  async function layers(dir, zonesFile, original) {
    const key = dir + '|' + (original ? 'sample' : zonesFile);
    if (cache.has(key)) return cache.get(key);
    if (pending.has(key)) return pending.get(key);
    const files = original ? ['sample.webp'] : ['sample.webp', zonesFile, 'shade.png'];
    const p = Promise.all(files.map(f => pixels(dir+'/'+f)))
      .then(([sample,zones,shade]) => {
        if (zones && (sample.w!==zones.w || sample.h!==zones.h || sample.w!==shade.w || sample.h!==shade.h))
          throw new Error('Garment layer sizes differ');
        const value={sample,zones,shade}; cache.set(key,value); if(cache.size>6)cache.delete(cache.keys().next().value); return value;
      }).finally(() => pending.delete(key));
    pending.set(key,p); return p;
  }
  function rgb(h) {
    if (!/^#[0-9a-f]{6}$/i.test(h)) throw new Error('Invalid preview colour');
    const n=parseInt(h.slice(1),16); return [n>>16&255,n>>8&255,n&255];
  }
  // Which part a zone id paints with, after the style's own settings.
  function resolver(cfg, o) {
    const alias = cfg.aliases || {}, band = cfg.bands;
    const solid = o.colours && o.colours.logoStyle === 'solid';
    const stripes = o.style === 'stripe' && o.colours ? VA_STRIPE.colours(o.colours) : [];
    return id => {
      if (band && id >= band.first && id < band.first + band.count) {
        const b = Math.min(stripes.length - 1, Math.floor((id - band.first) * stripes.length / band.count));
        return {area: b === 0 ? 'side' : 'stripe' + (b + 1), hex: stripes[b]};
      }
      let name = cfg.ids[id];
      if (!name || name === 'fixed') return null;
      name = alias[name] || name;
      if (name === 'logoOutline' && solid) name = cfg.under[id];
      return {area: name};
    };
  }
  async function render(canvas, options) {
    const token={}; requests.set(canvas,token);
    // Snapshot inputs: an earlier request must never paint a later design's labels.
    const o={...options,colours:structuredClone(options.colours||{})};
    const man=await manifest(), cfg=man[o.style]?.[o.view];
    if (!cfg) throw new Error('This garment view is unavailable');
    const right = o.style === 'stripe' && o.colours.stripePlacement === 'right' && cfg.zonesRight;
    const L=await layers(cfg.dir, right ? cfg.zonesRight : 'zones.png', cfg.original);
    if (requests.get(canvas)!==token) return false;
    const {w,h,data:source}=L.sample;
    const ctx=canvas.getContext('2d');
    canvas.width=w; canvas.height=h;
    const out=ctx.createImageData(w,h), dst=out.data;
    if (cfg.original) {
      dst.set(source); ctx.putImageData(out,0,0);
      painted.delete(canvas); canvas.dataset.rendered=o.style+'|'+o.view; canvas.dataset.mode='sample';
      return true;
    }
    const z=L.zones.data, shade=L.shade.data, part=resolver(cfg,o);
    // Per-id lookup: a colour, or the Ombre fade (-1). Built once per render, not per pixel.
    const palette=new Array(256).fill(null), fade=new Uint8Array(256);
    const fadePanels = VA_PARTS.fadePanels(o.style, o.colours);
    for (let id=0; id<256; id++) {
      const p=part(id); if (!p) continue;
      if (o.style==='ombre' && (p.area==='body' || p.area==='side' || (p.area==='panels' && fadePanels))) { fade[id]=1; continue; }
      palette[id]=rgb(p.hex || (p.area==='laces' ? o.laces : (o.colours[p.area]||'#101014')));
    }
    const stops=VA_OMBRE.stops(o.colours);
    const rows=o.style==='ombre' ? Array.from({length:h},(_,y)=>VA_OMBRE.sample(stops,(y/h-cfg.fade[0])/(cfg.fade[1]-cfg.fade[0]))) : null;
    for(let i=0;i<dst.length;i+=4) {
      const id=z[i];
      dst[i+3]=source[i+3];
      if(!source[i+3]) continue;
      const colour = o.original ? null : fade[id] ? rows[(i/4/w)|0] : palette[id];
      if(!colour) { dst[i]=source[i];dst[i+1]=source[i+1];dst[i+2]=source[i+2];continue; }
      const light=shade[i]/128;
      for(let k=0;k<3;k++) dst[i+k]=Math.min(255,colour[k]*light);
    }
    ctx.putImageData(out,0,0);
    painted.set(canvas,{L,cfg,style:o.style,part});
    canvas.dataset.rendered=o.style+'|'+o.view;
    canvas.dataset.mode=o.original?'sample':'design';
    return true;
  }
  function pick(canvas,clientX,clientY) {
    const p=painted.get(canvas); if(!p) return null;
    const rect=canvas.getBoundingClientRect();
    const x=Math.floor((clientX-rect.left)/rect.width*p.L.sample.w);
    const y=Math.floor((clientY-rect.top)/rect.height*p.L.sample.h);
    const i=(y*p.L.sample.w+x)*4;
    if(!p.L.sample.data[i+3]) return null;
    const zone=p.part(p.L.zones.data[i])?.area || null;
    return p.style==='ombre' && zone==='body' && y/p.L.sample.h>.58?'side':zone;
  }
  function prefetch(style,view,placement) {
    manifest().then(m=>{const c=m[style]?.[view];if(c)return layers(c.dir,placement==='right'&&c.zonesRight?c.zonesRight:'zones.png',c.original);}).catch(()=>{});
  }
  return {render,prefetch,pick};
})();
